class SQFavoritedItems extends HTMLElement {
  constructor() {
    super();
    this.isInitialized = false;
    this.products = [];
    this.container = null;
    this.emptyState = null;
    this.loadingState = null;
    this.swiper = null;
    
    // Swiper configuration from data attributes (following sq-carousel pattern)
    this.loop = this.dataset.loop !== "false";
    this.showNavigation = this.dataset.showNavigation !== "false";
    this.spaceBetween = parseInt(this.dataset.spaceBetween || 20);
    
    // Selectors for swiper elements (same as sq-carousel)
    this.selectors = {
      swiperContainer: ".swiper",
      swiperWrapper: ".swiper-wrapper",
      prevButton: ".sq-custom-navigation-prev",
      nextButton: ".sq-custom-navigation-next"
    };
    
    console.log('🔧 SQFavoritedItems constructor called');
  }

  connectedCallback() {
    console.log('🔌 SQFavoritedItems connected to DOM');
    
    // Now we can query DOM elements since they exist
    this.container = this.querySelector('.sq-favorited-items__container');
    this.emptyState = this.querySelector('.sq-favorited-items__empty');
    this.loadingState = this.querySelector('.sq-favorited-items__loading');
    
    console.log('📋 Found elements:', {
      container: !!this.container,
      emptyState: !!this.emptyState,
      loadingState: !!this.loadingState
    });
    
    this.init();
  }

  async init() {
    if (this.isInitialized) return;
    
    console.log('🚀 Initializing SQFavoritedItems');
    
    this.showLoading();
    
    // Setup event listeners (this will handle wishlist data when ready)
    this.setupEventListeners();
    
    this.isInitialized = true;
    console.log('✅ SQFavoritedItems initialization complete');
  }



  async renderWishlistProducts(wishlistData) {
    if (!this.container) {
      console.warn("⚠️ SQFavoritedItems: Container not found");
      return;
    }

    console.log("📋 SQFavoritedItems: Wishlist data to render:", wishlistData);

    // Show loading state
    this.showLoading();

    try {
      // Fetch product data for all wishlist items (same pattern as cart-drawer-navigation)
      const productPromises = [];
      
      for (const [key, value] of Object.entries(wishlistData)) {
        console.log("🔍 SQFavoritedItems: Processing wishlist item:", key, value);
        
        // Extract product ID from the key or value structure (same pattern as cart-drawer-navigation)
        let productId = null;
        
        if (typeof value === 'object' && value.productId) {
          productId = value.productId;
        } else if (typeof value === 'string' || typeof value === 'number') {
          productId = value;
        } else if (typeof key === 'string' || typeof key === 'number') {
          productId = key;
        }

        if (productId) {
          productPromises.push(this.getProductData(productId));
        }
      }

      // Wait for all product data to be fetched
      const products = await Promise.all(productPromises);
      
      console.log("🔍 SQFavoritedItems: Products:", products);
      
      // Filter out null products
      this.products = products.filter(product => product !== null);
      
      if (this.products.length === 0) {
        console.log('😞 SQFavoritedItems: No valid products found, showing empty state');
        this.showEmptyState();
        return;
      }

      console.log(`✅ SQFavoritedItems: Successfully loaded ${this.products.length} products`);
      
      // Process products with wishlist variants (same pattern as cart-drawer-navigation)
      const productsWithWishlistVariants = [];
      
      this.products.forEach((product) => {
        if (product) {
          // Get variants that are actually in wishlist
          const wishlistVariants = [];
          
          for (const variant of product.variants) {
            if (this.isVariantInWishlist(variant.id, wishlistData)) {
              wishlistVariants.push(variant);
            }
          }
          
          if (wishlistVariants.length > 0) {
            productsWithWishlistVariants.push({
              ...product,
              wishlistVariants: wishlistVariants
            });
          }
        }
      });

      if (productsWithWishlistVariants.length === 0) {
        console.log('😞 SQFavoritedItems: No wishlist variants found, showing empty state');
        this.showEmptyState();
        return;
      }
      console.log("🔍 Dqwdwqdqdts with wishlist variants:", productsWithWishlistVariants);

      // Render products
      await this.renderProducts(productsWithWishlistVariants);
      
    } catch (error) {
      console.error("❌ SQFavoritedItems: Error rendering wishlist items:", error);
      this.showEmptyState();
    }
  }

  async getProductData(productId) {
    try {
      // Use the Storefront API to fetch product data (same pattern as cart-drawer-navigation)
      const product = await window.MinimogTheme.StorefrontAPI.helpers.fetchProductForCart(productId);
      return product;
    } catch (error) {
      console.error(`❌ SQFavoritedItems: Error fetching product data for ID ${productId}:`, error);
      return null;
    }
  }

  isVariantInWishlist(variantId, wishlistData) {
    // Check if variant is in wishlist (same pattern as cart-drawer-navigation)
    for (const [key, value] of Object.entries(wishlistData)) {
      if (value.variants && value.variants.includes(variantId.toString())) {
        return true;
      }
    }
    return false;
  }

  async renderProducts(productsWithWishlistVariants) {
    if (!this.container) return;
    
    // Clear container
    this.container.innerHTML = '';
    
    // Show products container
    this.hideLoading();
    this.hideEmptyState();
    this.container.style.display = 'block';
    
    // Create swiper structure
    this.createSwiperStructure();
    
    // Render each product using the product card snippet
    const swiperWrapper = this.container.querySelector(this.selectors.swiperWrapper);
    
    for (const product of productsWithWishlistVariants) {
      await this.renderProductCard(product, swiperWrapper);
    }
    
    // Initialize swiper after products are rendered
    this.initSwiper();
  }

  createSwiperStructure() {
    this.container.innerHTML = `
      <div class="sq-favorited-items__swiper swiper">
        <div class="sq-favorited-items__swiper-wrapper swiper-wrapper">
          <!-- Products will be rendered here -->
        </div>
        
        ${this.showNavigation ? `
          <div class="sq-custom-navigation-container">
            <div class="sq-custom-navigation-prev">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
              </svg>  
            </div>
            <div class="sq-custom-navigation-next">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
              </svg>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  async renderProductCard(product, swiperWrapper) {
    // console.log('🔍 Rendering product card:', product, variant);
    const slide = document.createElement('a');
    
    slide.className = 'sq-favorited-items__item swiper-slide';
    
    const textColor = this.getAttribute('data-text-color') || '#000000';
    const productUrl = `/products/${product.handle}`;
    const featuredImage = product.featured_image || null;
    slide.href = productUrl;
    // Format price
    const variant = product.wishlistVariants[0];
    const price = this.formatMoney(variant.price);
    const compareAtPrice = variant.compare_at_price > variant.price ? this.formatMoney(variant.compare_at_price) : null;
    
    slide.innerHTML = `
      <div class="sq-product-card" data-product-id="${product.id}">
        <div class="wishlist-engine" data-product_id="${product.id}" data-variant_id="${variant.id}" data-full_button="false" data-css="true"></div>
        <a class="sq-product-card__media" href="${productUrl}">
          ${featuredImage ? `
            <div class="sq-product-card__main-image">
              <responsive-image class="m-image" style="--aspect-ratio: 1">
                <img src="${featuredImage.url}" alt="${featuredImage.alt}" loading="lazy" class="m:w-full m:h-full">
              </responsive-image>
            </div>
          ` : `
            <div style="--aspect-ratio: 1">
              <svg class="m-placeholder-svg" viewBox="0 0 525 525" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="525" height="525" fill="#F6F6F6"/>
                <path d="M210 262.5H315M262.5 210V315" stroke="#999" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
          `}
        </a>
        
        <div class="sq-product-card__content m:text-left" style="cursor: default;">
          <div class="sq-product-card__info">
          <a href="${productUrl}" style="cursor: pointer;">
            <h3 class="sq-product-card__title">
              ${product.title.split(' - ').length === 2 ? product.title.split(' - ')[1] : product.title.split(' - ')[0]}
            </h3>
            </a>
            <p class="sq-product-card__subtitle">
              <span style="color: ${textColor};">${product.title.split(' - ')[0]}</span>
            </p>
            
            <div class="sq-product-card__price">
              ${compareAtPrice ? `
                <span class="m-price m-price--regular" style="color: ${textColor};">${compareAtPrice}</span>
                <span class="m-price m-price--sale" style="color: ${textColor};">${price}</span>
              ` : `
                <span class="m-price" style="color: ${textColor};">${price}</span>
              `}
            </div>
          </div>
        </div>
      </div>
    `;
    
    swiperWrapper.appendChild(slide);
    
    // Initialize title overflow animation for this product card
    this.initializeTitleAnimation(slide.querySelector('.sq-product-card__title span'));
  }

  async addToCart(button) {
    const productId = button.getAttribute('product-id');
    const variantId = button.getAttribute('variant-id');
    
    if (!variantId) {
      console.error("❌ No variant ID provided for add to cart");
      return;
    }

    // Show loading state
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;

    try {
      // Prepare request data
      let requestData = {
        'items': [{
          'id': parseInt(variantId),
          'quantity': 1
        }]
      };

      // Get cart sections to update if cart drawer exists
      const cart = document.querySelector('m-cart-drawer');
      if (cart) {
        requestData.sections = cart.getSectionsToRender().map(section => section.id);
        requestData.sections_url = window.location.pathname;
      }

      // Make the add to cart request
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestData)
      };

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', config);
      const responseData = await response.json();

      if (responseData.status) {
        // Handle error
        console.error("❌ Add to cart error:", responseData.message);
        
        // Show error notification
        if (window.MinimogTheme && window.MinimogTheme.Notification) {
          window.MinimogTheme.Notification.show({
            target: document.body,
            method: 'appendChild',
            type: 'error',
            message: responseData.message || 'Error adding item to cart',
            last: 3000,
            sticky: true
          });
        }
        
        // Emit error event
        if (window.MinimogEvents) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartError, {
            source: 'favorited-items-add-to-cart',
            productVariantId: variantId,
            errors: responseData.description,
            message: responseData.message
          });
        }
      } else {
        // Success - handle cart update
        console.log("✅ Item added to cart successfully");

        if (cart && window.MinimogSettings.enable_cart_drawer) {
          // Update cart drawer content
          cart.renderContents(responseData);
          
          // Show success notification in cart drawer
          if (window.MinimogTheme && window.MinimogTheme.Notification) {
            window.MinimogTheme.Notification.show({
              target: cart.querySelector('m-cart-drawer-items'),
              method: 'prepend',
              type: 'success',
              message: window.MinimogStrings.itemAdded || 'Item added to cart',
              delay: 400
            });
          }

          // Open cart drawer
          if (window.MinimogEvents) {
            window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
          }
        } else {
          // Show success notification
          if (window.MinimogTheme && window.MinimogTheme.Notification) {
            window.MinimogTheme.Notification.show({
              target: document.body,
              method: 'appendChild',
              type: 'success',
              message: window.MinimogStrings.itemAdded || 'Item added to cart',
              last: 3000,
              sticky: true
            });
          }

          // Update cart count bubble
          const response = await fetch(window.Shopify.routes.root + 'cart.json');
          const cartData = await response.json();
          
          if (cartData) {
            const count = cartData.item_count;
            const cartCounts = document.querySelectorAll('.m-cart-count-bubble');
            cartCounts.forEach(cartCount => {
              if (count > 0) {
                cartCount.textContent = count;
                cartCount.classList.remove('m:hidden');
              } else {
                cartCount.classList.add('m:hidden');
              }
            });
          }
        }

        // Emit cart update event
        if (window.MinimogEvents) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, responseData);
        }

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('product-ajax:added', {
          detail: {
            product: responseData
          }
        }));
      }

    } catch (error) {
      console.error("❌ Add to cart request failed:", error);
      
      // Show error notification
      if (window.MinimogTheme && window.MinimogTheme.Notification) {
        window.MinimogTheme.Notification.show({
          target: document.body,
          method: 'appendChild',
          type: 'error',
          message: 'Failed to add item to cart. Please try again.',
          last: 3000,
          sticky: true
        });
      }
    } finally {
      // Reset button state
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  formatMoney(cents) {
    // Basic money formatting - this should ideally use Shopify's money format
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  initializeTitleAnimation(titleSpan) {
    if (!titleSpan) return;
    
    const parent = titleSpan.parentElement;
    
    // Check if content fits
    if (titleSpan.offsetWidth <= parent.offsetWidth) {
      titleSpan.classList.add('fitting-content');
    } else {
      titleSpan.classList.remove('fitting-content');
      
      // Calculate scroll amount
      const overflowAmount = titleSpan.offsetWidth - parent.offsetWidth;
      const scrollAmountWithBuffer = -(overflowAmount + 10) + 'px';
      
      titleSpan.style.setProperty('--scroll-amount', scrollAmountWithBuffer);
      
      // Calculate animation duration
      const baseDuration = 2;
      const additionalDuration = overflowAmount / 150;
      const totalDuration = baseDuration + additionalDuration;
      
      titleSpan.style.setProperty('--product-card-animation-duration', `${totalDuration}s`);
    }
  }

  setupEventListeners() {
    // Listen for wishlist header ready event (same pattern as cart-drawer-navigation)
    document.addEventListener('wishlist:header:ready', (event) => {
      console.log("🎉 SQFavoritedItems: Wishlist header ready event received");
      const wishlistData = event.detail.localWishlistIds;
      console.log("📋 SQFavoritedItems: Initial wishlist data from WishlistHeader:", wishlistData);
      this.onWishlistDataReady(wishlistData);
    });

    // Listen for wishlist header updates (same pattern as cart-drawer-navigation)
    document.addEventListener('wishlist:header:updated', (event) => {
      console.log("🔄 SQFavoritedItems: Wishlist header updated event received");
      const { action, productId, variantId, localWishlistIds } = event.detail;
      console.log(`📋 SQFavoritedItems: Wishlist ${action}:`, { productId, variantId });
      console.log("📋 SQFavoritedItems: Updated wishlist data:", localWishlistIds);
      this.onWishlistDataUpdated(action, productId, variantId, localWishlistIds);
    });

    // If wishlist header is already ready, get the data immediately (same pattern as cart-drawer-navigation)
    if (window.WishlistHeader && window.WishlistHeader.localWishlistIds) {
      console.log("📋 SQFavoritedItems: Wishlist header already ready, getting data immediately");
      this.onWishlistDataReady(window.WishlistHeader.localWishlistIds);
    }
    
    // Listen for window resize to recalculate title animations and update swiper
    window.addEventListener('resize', () => {
      this.recalculateTitleAnimations();
      this.handleResize();
    });
    
    // For theme editor (similar to sq-carousel)
    if (Shopify && Shopify.designMode) {
      document.addEventListener("shopify:section:load", (event) => {
        if (event.target.contains(this)) {
          this.initSwiper();
        }
      });
    }
  }

  recalculateTitleAnimations() {
    const titleSpans = this.querySelectorAll('.sq-product-card__title span');
    titleSpans.forEach(span => {
      this.initializeTitleAnimation(span);
    });
  }

  onWishlistDataReady(wishlistData) {
    // Handle initial wishlist data (same pattern as cart-drawer-navigation)
    console.log("✅ SQFavoritedItems: Wishlist data ready:", wishlistData);
    this.wishlistData = wishlistData;
    this.renderWishlistProducts(wishlistData);
  }

  onWishlistDataUpdated(action, productId, variantId, wishlistData) {
    // Handle wishlist data updates (same pattern as cart-drawer-navigation)
    console.log(`🔄 SQFavoritedItems: Handling wishlist ${action} for product ${productId}, variant ${variantId}`);
    console.log("📋 SQFavoritedItems: Current wishlist state:", wishlistData);
    this.wishlistData = wishlistData;
    this.renderWishlistProducts(wishlistData);
  }

  showLoading() {
    console.log('⏳ Showing loading state');
    if (this.loadingState) {
      this.loadingState.style.display = 'block';
    } else {
      console.warn('⚠️ Loading state element not found');
    }
    if (this.container) {
      this.container.style.display = 'none';
    }
    if (this.emptyState) {
      this.emptyState.style.display = 'none';
    }
  }

  hideLoading() {
    console.log('✅ Hiding loading state');
    if (this.loadingState) {
      this.loadingState.style.display = 'none';
    }
  }

  showEmptyState() {
    console.log('😞 Showing empty state');
    this.hideLoading();
    if (this.container) {
      this.container.style.display = 'none';
    }
    if (this.emptyState) {
      this.emptyState.style.display = 'block';
    } else {
      console.warn('⚠️ Empty state element not found');
    }
  }

  hideEmptyState() {
    console.log('✅ Hiding empty state');
    if (this.emptyState) {
      this.emptyState.style.display = 'none';
    }
  }

  initSwiper() {
    const swiperContainer = this.container.querySelector(this.selectors.swiperContainer);
    if (!swiperContainer) return;
    
    // If swiper already initialized, destroy it first
    if (swiperContainer.swiper) {
      swiperContainer.swiper.destroy(true, true);
    }
    
    const prevButton = this.container.querySelector(this.selectors.prevButton);
    const nextButton = this.container.querySelector(this.selectors.nextButton);
    
    // Initialize Swiper (following sq-carousel pattern)
    this.swiper = new MinimogLibs.Swiper(swiperContainer, {
      slidesPerView: "auto",
      spaceBetween: this.spaceBetween,
      loop: this.loop,
      
      navigation: this.showNavigation ? {
        nextEl: nextButton,
        prevEl: prevButton
      } : false,
      
      on: {
        init: () => {
          // Make carousel visible after initialization
          swiperContainer.classList.add('swiper-initialized');
        }
      }
    });

    if (this.swiper) {
      console.log("swiper favorited items initialized:", this.swiper);
    }
    
    // Add event listeners for navigation if not using built-in navigation
    if (this.swiper && !this.swiper.navigation && this.showNavigation) {
      prevButton && prevButton.addEventListener("click", () => this.swiper.slidePrev());
      nextButton && nextButton.addEventListener("click", () => this.swiper.slideNext());
    }
  }

  handleResize() {
    // Update swiper on resize if needed
    if (this.swiper) {
      this.swiper.update();
    }
  }
}

// Register the custom element
console.log('🔧 Registering sq-favorited-items custom element');

if (customElements.get('sq-favorited-items')) {
  console.log('⚠️ sq-favorited-items already registered');
} else {
  customElements.define('sq-favorited-items', SQFavoritedItems);
  console.log('✅ sq-favorited-items custom element registered successfully');
} 